import axiosClient from 'api/axiosClient';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import AgencySaleDialogView from 'features/Agency/components/AgencySaleDialogView';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown, { KTBSDropdownDirections } from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AgencySaleOrderListFilterView from './components/AgencySaleOrderListFilterView';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppConstants from 'general/constants/AppConstants';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';

AgencySaleOrderListView.propTypes = {
  agencyId: PropTypes.string,
  showFullUI: PropTypes.bool,
  orderMode: PropTypes.oneOf(['BUY', 'SELL', '']),
  prefKey: PropTypes.string,
  month: PropTypes.string,
  buyerId: PropTypes.string,
  defaultOrderType: PropTypes.string,
  additionalComponent: PropTypes.element,
};

function AgencySaleOrderListView({ agencyId, showFullUI = false, orderMode = '', prefKey, month = '', buyerId, defaultOrderType = '', additionalComponent = <></> }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const operatorList = useSelector((state) => state?.app?.appConfig?.operators) ?? [];
  const { user, agencyRoleList, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isAgency = user?.role === 'AGENCY';
  const isSale = user?.agency?.primaryRole === AgencyConstants.AgencyRole.SALE;

  const defaultColumnIds = useMemo(() => {
    return [
      'SALE_ID',
      'OPERATOR_ID',
      'USER_ID',
      'AGENCY_ID',
      'BUYER_ID',
      'ORDER_TYPE',
      'AMOUNT',
      'STATUS',
      'STATUS_CONTRACT',
      'CONTRACT',
      'POA_DOCUMENT',
      'CONTRACT_NUMBER',
      'INVOICE_STATUS',
      'INVOICE',
      'TIME',
      'COMPLETED_TIME'
    ];
  }, []);
  const [filter, setFilter] = useState();
  useEffect(() => {
    let filter = {};

    if (Utils.isValid(prefKey)) {
      filter = AgencyHelper.Filter.saleOrderList;
    } else {
      const [fromDate, toDate] = getDateFilterOption(dateFilter.baMuoiNgayGanNhat) || [];

      filter = {
        page: 0,
        limit: 10,
        q: '',
        status: '',
        orderType: '',
        contractStatus: '',
        dateFilter: dateFilter.baMuoiNgayGanNhat,
        from: fromDate?.format('YYYY-MM-DD') || moment().subtract(30, 'days').format('YYYY-MM-DD'),
        to: toDate?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD'),
      };
    }

    if (month && typeof month === 'string' && month.length) {
      try {
        const startOfMonth = moment(month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
        filter = {
          ...filter,
          dateFilter: dateFilter.tuyChon,
          from: startOfMonth,
          to: endOfMonth,
          status: 'COMPLETED',
        };
      } catch (error) {
        console.error('Error parsing month:', error);
      }
    }

    if (agencyId && typeof agencyId === 'string' && agencyId.length) {
      filter = {
        ...filter,
        agencyId: agencyId,
      };
    }

    if (buyerId && typeof buyerId === 'string' && buyerId.length) {
      filter = {
        ...filter,
        buyerId: buyerId,
      };
    }

    if (defaultOrderType && typeof defaultOrderType === 'string' && defaultOrderType.length) {
      filter = {
        ...filter,
        orderType: defaultOrderType,
      };
    }

    setFilter(filter);
  }, [prefKey, agencyId, orderMode, month, buyerId, defaultOrderType]);

  const [agencySaleList, setAgencySaleList] = useState([]);
  const [total, setTotal] = useState(0);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'SALE_ID',
        name: UIHelper.buildTableColumn('Mã đơn hàng'),
        selector: (row) => row?.saleCode ?? row?.saleId,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OPERATOR_ID',
        name: UIHelper.buildTableColumn('Nhà điều hành'),
        selector: (row) => _.find(operatorList, { operatorId: row?.operatorId })?.name,
        minWidth: '200px',
        style: tableStyle.columns.normal,
        omit: orderMode === 'BUY',
      },
      {
        id: 'AGENCY_ID',
        name: UIHelper.buildTableColumn('Người bán hàng/giới thiệu'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.agency?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                AgencyHelper.Navigation.navigateToAgencyDetail(null, row?.agency);
              }}
            >
              {row?.agency?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'BUYER_ID',
        name: UIHelper.buildTableColumn('Nhà đầu tư/Chủ MB'),
        omit: orderMode === 'BUY',
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              AgencyHelper.Navigation.navigateToAgencyDetail(null, row?.buyer);
            }}
          >
            <KTTooltip offset={[0, 0]} text={row?.buyer?.name}>
              <span>
                {row?.buyer?.name}
              </span>
            </KTTooltip>
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'ORDER_TYPE',
        name: UIHelper.buildTableColumn('Loại đơn hàng'),
        selector: (row) => AgencyHelper.Utils.getSaleOrderTypeName(row?.orderType),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AMOUNT',
        name: UIHelper.buildTableColumn('Tổng tiền', 'VNĐ'),
        selector: (row) => row?.orderType !== 'LANDLORD'
          ? row?.amountAfterTax
            ? Utils.formatNumber(row?.amountAfterTax ?? 0)
            : Utils.formatNumber(row?.amount ?? 0)
          : '',
        right: true,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái đơn hàng'),
        selector: (row) => (
          <KTLabel
            text={AgencyHelper.Utils.getSaleOrderStatusName(row?.status, row?.orderType)}
            isInline={true}
            size={KTLabelSize.medium}
            additionalClassName={`label-light-${AgencyHelper.Utils.getSaleOrderStatusColor(
              row?.status
            )}`}
          />
        ),
        minWidth: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS_CONTRACT',
        name: UIHelper.buildTableColumn('Trạng thái hợp đồng'),
        selector: (row) => {
          return !!_.first(row?.contracts) ? (
            <KTLabel
              text={AgencyHelper.Utils.getContractStatusName(_.first(row?.contracts)?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${AgencyHelper.Utils.getContractStatusColor(
                _.first(row?.contracts)?.status
              )}`}
              additionalDescription={_.first(row?.contracts)?.cancelingReason}
            />
          ) : (
            <></>
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONTRACT',
        name: UIHelper.buildTableColumn('Hợp đồng'),
        selector: (row) => {
          return row?.contracts?.length > 0 ? (
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                console.log({ user, row });
                if (isAdmin || user?.agency?.agencyId === row?.buyerId) {
                  const contract = _.first(row?.contracts)?.contractDocument;
                  if (Utils.isValid(contract)) {
                    const token = user?.token;
                    let url = Utils.getFullUrl(contract);

                    if (token) {
                      url += (url.includes('?') ? '&' : '?') + `token=${token}`;
                    }

                    Utils.openInNewTab(url);
                  }
                } else {
                  ToastHelper.showError('Bạn không có quyền xem hợp đồng này');
                }
              }}
            >
              Xem chi tiết
            </span>
          ) : (
            <></>
          );
        },
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'POA_DOCUMENT',
        name: UIHelper.buildTableColumn('Giấy uỷ quyền'),
        selector: (row) => {
          return !!_.first(row?.contracts)?.poaDocument ? (
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                if (isAdmin || user?.agency?.agencyId === row?.buyerId) {
                  const contract = _.first(row?.contracts)?.poaDocument;
                  if (Utils.isValid(contract)) {
                    const token = user?.token;
                    let url = Utils.getFullUrl(contract);

                    if (token) {
                      url += (url.includes('?') ? '&' : '?') + `token=${token}`;
                    }

                    Utils.openInNewTab(url);
                  }
                } else {
                  ToastHelper.showError('Bạn không có quyền xem giấy uỷ quyền này');
                }
              }}
            >
              Xem chi tiết
            </span>
          ) : (
            <></>
          );
        },
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CONTRACT_NUMBER',
        name: UIHelper.buildTableColumn('Số hợp đồng'),
        selector: (row) => row?.contractNumber,
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'INVOICE_STATUS',
        name: UIHelper.buildTableColumn('Trạng thái XHĐ'),
        selector: (row) => !!row?.invoiceSerial ? 'Đã xuất' : 'Chưa xuất',
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'INVOICE',
        name: UIHelper.buildTableColumn('Hóa đơn'),
        selector: (row) => {
          return row?.invoiceDocument?.length > 0 ? (
            <span
              className="hover-opacity-60 cursor-pointer"
              onClick={() => {
                handleViewFiles(row?.invoiceDocument);
              }}
            >
              {`Xem chi tiết (${row?.invoiceDocument?.length})`}
            </span>
          ) : (
            <></>
          );
        },
        width: '160px',
        style: tableStyle.columns.normal,
        omit: !isAdmin && orderMode !== 'BUY',
      },
      {
        id: 'USER_ID',
        name: UIHelper.buildTableColumn('Người tạo đơn'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.user?.fullname}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'TIME',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'COMPLETED_TIME',
        name: UIHelper.buildTableColumn('Ngày hoàn thành'),
        selector: (row) => row?.status === AgencyConstants.SaleOrderStatus.COMPLETED
          ? Utils.formatDateTimeToString(row?.time, 'DD/MM/YYYY - HH:mm')
          : '',
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: false,
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [operatorList, user, orderMode, isAdmin]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  useEffect(() => {
    if (Utils.isValid(filter)) {
      let params = { ...filter };
      params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');
      if (params.timeFilterType === 'CREATED_DATE') {
        params = _.omit(params, ['dateFilter', 'from', 'to', 'timeFilterType', 'filterDateFilter'])
      } else {
        params = _.omit(params, ['createdDateFilter', 'createdFrom', 'createdTo', 'timeFilterType', 'filterDateFilter'])
      }
      if (isAdmin || isSale || orderMode === 'SELL') {
        params.agencyId = agencyId;
        params.buyerId = buyerId;
        params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');
        requestGetAgencySaleList(params);
      } else {
        AgencyHelper.API.requestGetMySaleList(params).then((res) => {
          if (res) {
            setAgencySaleList(res.orders);
            setTotal(res.total);
          }
        });
      }
    }
  }, [filter, isAdmin, isSale, orderMode]);
  const windowSize = useWindowSize();

  // MARK: ---- METHODS ----
  const requestGetAgencySaleList = async (params) => {
    const filteredParams = _.pickBy(params, (value) => value !== '');
    try {
      const res = await axiosClient.get('/sale', { params: filteredParams });
      const { result, sales, total } = res;
      if (result === 'success') {
        setAgencySaleList(sales);
        setTotal(total);
      }
    } catch (error) { }
  };
  const handleRefresh = (newFilter) => {
    if (Utils.isValid(prefKey)) {
      AgencyHelper.Filter.saleOrderList = {
        ...AgencyHelper.Filter.saleOrderList,
        ...newFilter,
      };
      setFilter(AgencyHelper.Filter.saleOrderList);
    } else {
      setFilter(newFilter);
    }
  };

  const buildMoreActions = (row) => {
    const contract = _.first(row?.contracts);
    const isMine = user?.userId === row?.userId;
    const isSeller = user?.agency?.agencyId === row?.agencyId;
    const actions = [];
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.orderView.code)) {
      // xem chi tiet
      actions.push({
        text: 'Xem chi tiết',
        iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleShowOrderDetail(row?.saleId);
        },
      });
    }
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.orderUpdate.code)) {
      // duyet thanh toan
      if (
        isAdmin &&
        ((row?.orderType === AgencyConstants.SaleOrderType.GROUP &&
          row?.status === AgencyConstants.SaleOrderStatus.ORDERED) ||
          (row?.orderType === AgencyConstants.SaleOrderType.INVESTOR &&
            row?.status === AgencyConstants.SaleOrderStatus.ORDERED &&
            _.first(row?.contracts)?.status === AgencyConstants.ContractStatus.COMPLETED))
      )
        actions.push({
          text: row?.orderType === AgencyConstants.SaleOrderType.GROUP ? 'Duyệt thanh toán' : 'Duyệt tạm ứng',
          iconElement: (
            <i
              className="fa-regular fa-sack-dollar"
              style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
            />
          ),
          click: () => {
            AppCustomDialogHelper.show(
              '',
              <AgencySaleDialogView
                iconEl={<img src={AppResource.images.dialogIcons.dialogUpdateOrder} />}
                title="Cập nhật đơn hàng"
                contentEl={<>Chuyển trạng thái đơn hàng thành đã thanh toán?</>}
                onCancel={() => {
                  AppCustomDialogHelper.hide();
                }}
                onConfirm={async () => {
                  AppCustomDialogHelper.hide();
                  const result = await AgencyHelper.API.requestUpdateSale(row?.saleId, {
                    status: AgencyConstants.SaleOrderStatus.PAID,
                  });
                  if (result) {
                    ToastHelper.showSuccess('Cập nhật đơn hàng thành công');
                    handleRefresh({ ...filter });
                  }
                }}
              />,
              'md',
              false,
              true
            );
          },
        });
      if (isAdmin || ((isMine || isSeller)))
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            router.navigate(`${AppRoute.orderList}/add/${row?.saleId}`);
          },
        });
      if (
        (isAdmin && row?.status !== AgencyConstants.SaleOrderStatus.CANCELLED
          || (isSeller || isMine) && row?.status === AgencyConstants.SaleOrderStatus.ORDERED && !row?.contracts?.length)
        && Utils.checkPermission(permissionList, AppConstants.permissionCode.orderDelete.code)
        //  &&
        // [
        //   AgencyConstants.SaleOrderStatus.ORDERED,
        //   AgencyConstants.SaleOrderStatus.SHIPPING,
        //   AgencyConstants.SaleOrderStatus.INSTALLING,
        //   AgencyConstants.SaleOrderStatus.INSTALLED,
        //   AgencyConstants.SaleOrderStatus.PAID,
        //   AgencyConstants.SaleOrderStatus.SIGNED,
        // ].includes(row?.status)
      ) {
        actions.push({
          text: 'Hủy đơn hàng',
          iconElement: (
            <i
              className="fa-regular fa-times-circle"
              style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
            />
          ),
          click: () => {
            AppCustomDialogHelper.show(
              '',
              <AgencySaleDialogView
                iconEl={<img src={AppResource.images.dialogIcons.dialogError} />}
                title="Hủy đơn hàng"
                contentEl={
                  <>
                    Bạn có chắc muốn hủy đơn hàng:{' '}
                    <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                      {row?.saleCode ?? row?.saleId}
                    </span>
                    ?
                  </>
                }
                onCancel={() => {
                  AppCustomDialogHelper.hide();
                }}
                onConfirm={async () => {
                  // const uncancelledContract = row?.contracts?.find(contract => contract?.status !== AgencyConstants.ContractStatus.CANCELLED);
                  // if (!!uncancelledContract) {
                  //   if (uncancelledContract?.status === AgencyConstants.ContractStatus.EXPIRED) {
                  //     ToastHelper.showError(`Không thể hủy đơn hàng vì tồn tại hợp đồng ${uncancelledContract?.contractNumber} đã hết hạn`)
                  //   } else {
                  //     ToastHelper.showError(`Không thể hủy đơn hàng vì tồn tại hợp đồng ${uncancelledContract?.contractNumber} đang chạy`)
                  //   }
                  // } else {
                  AppCustomDialogHelper.hide();
                  let updateProductSuccess = true;
                  let updateContractSuccess = true;
                  if (row?.orderType === AgencyConstants.SaleOrderType.LANDLORD) {
                    updateProductSuccess = await AgencyHelper.API.requestUpdatePremise(
                      row?.premise?.premiseId,
                      Utils.transformEmptyStrings({
                        agencyId: null,
                      })
                    );
                    if (!updateProductSuccess) {
                      ToastHelper.showSuccess(`Cập nhật thông tin mặt bằng không thành công`);
                    }
                  }
                  const uncancelledContract = row?.contracts?.find(contract => contract?.status !== AgencyConstants.ContractStatus.CANCELLED);
                  if (!!uncancelledContract) {
                    updateContractSuccess = await AgencyHelper.API.requestUpdateContract(uncancelledContract?.contractId, {
                      status: AgencyConstants.ContractStatus.CANCELLED,
                      cancelingReason: "Hủy đơn hàng",
                    });
                    if (!updateContractSuccess) {
                      ToastHelper.showError(`Cập nhật trạng thái hợp đồng không thành công`);
                    }
                  }
                  if (updateProductSuccess && updateContractSuccess) {
                    const result = await AgencyHelper.API.requestUpdateSale(row?.saleId, {
                      status: AgencyConstants.SaleOrderStatus.CANCELLED,
                    });
                    if (result && _.isBoolean(result)) {
                      ToastHelper.showSuccess('Hủy đơn hàng thành công');
                      handleRefresh({ ...filter });
                    } else {
                      ToastHelper.showError('Hủy đơn hàng thất bại');
                    }
                  }
                  // }
                }}
              />,
              'md',
              false,
              true
            );
          },
        });
      }
    }
    if (
      (isAdmin || isMine || isSeller) &&
      (row?.status === AgencyConstants.SaleOrderStatus.ORDERED && !Utils.isValid(contract))
      && Utils.checkPermission(permissionList, AppConstants.permissionCode.orderDelete.code)
    )
      actions.push({
        text: 'Xoá đơn hàng',
        iconElement: (
          <i
            className="fa-regular fa-trash-can"
            style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
          />
        ),
        click: () => {
          AppCustomDialogHelper.show(
            '',
            <AgencySaleDialogView
              iconEl={<img src={AppResource.images.dialogIcons.dialogDeleteVehicle} />}
              title="Xoá đơn hàng"
              contentEl={
                <>
                  Bạn có chắc muốn xoá đơn hàng:{' '}
                  <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                    {row?.saleCode ?? row?.saleId}
                  </span>
                  ?
                </>
              }
              onCancel={() => {
                AppCustomDialogHelper.hide();
              }}
              onConfirm={async () => {
                AppCustomDialogHelper.hide();
                const result = await AgencyHelper.API.requestDeleteSale(row?.saleId);
                if (result && _.isBoolean(result)) {
                  ToastHelper.showSuccess('Xoá đơn hàng thành công');
                  handleRefresh({ ...filter });
                } else {
                  ToastHelper.showError(result || 'Xoá đơn hàng thất bại');
                }
              }}
            />,
            'md',
            false,
            true
          );
        },
      });

    return actions;
  };
  const handleShowOrderDetail = (saleId) => {
    router.navigate(`${saleId}`);
  };
  const handleViewFiles = (files) => {
    AppCustomDialogHelper.show(
      'Tệp đính kèm',
      <FileUploadView
        pathList={files}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
      />,
      'md',
      false,
      true
    );
  };

  return (
    <div className="">
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          {showFullUI ? (
            <div className="px-4 pt-4 pb-4 d-flex justify-content-between align-items-center">
              {/* title */}
              <div className="d-flex flex-column align-items-start">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  {orderMode === 'SELL'
                    ? 'Đơn hàng nhà đầu tư'
                    : orderMode === 'BUY'
                      ? 'Đơn hàng của tôi'
                      : 'Quản lý đơn hàng'}
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={{ color: AppColors.primary }}>
                    {Utils.formatNumber(total)} đơn hàng
                  </span>
                </span>
              </div>

              {/* actions */}
              {(isAdmin || isSale || orderMode === 'SELL')
                && Utils.checkPermission(permissionList, AppConstants.permissionCode.orderCreate.code)
                ? (
                  UIHelper.buildGreenButton(
                    'Tạo đơn hàng',
                    () => {
                      if (!isAdmin && !agencyRoleList?.includes('SALE')) {
                        ToastHelper.showError('Chức năng này không khả dụng do NĐT chưa ký hợp đồng môi giới với nhà điều hành')
                      } else {
                        router.navigate(`add`);
                      }
                    },
                    <i className="fa-regular fa-circle-plus" />
                  )
                ) : (
                  <></>
                )}
            </div>
          ) : (
            <div className="mb-4"></div>
          )}

          {/* table */}
          <div>
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              {windowSize.width >= 768 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Theo mã đơn hàng, người bán hàng/giới thiệu, người tạo đơn..."
                    value={AgencyHelper.Filter.saleOrderList.q}
                    onSubmit={(text) => {
                      handleRefresh({
                        ...filter,
                        page: 0,
                        q: text,
                      });
                    }}
                  />
                </div>
              )}

              {/* loc theo loai don hang */}
              {windowSize.width >= 1120 && !defaultOrderType?.length && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Loại đơn hàng')}
                  <KTBSDropdown
                    name="orderType"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(AgencyHelper.Data.orderTypeOptions, { value: filter?.orderType })
                          ?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.orderType}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...AgencyHelper.Data.orderTypeOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      handleRefresh({
                        ...filter,
                        page: 0,
                        orderType: value,
                      });
                    }}
                    isDropdownForm={true}
                    direction={showFullUI ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  />
                </div>
              )}

              {/* loc theo trang thai don hang */}
              {windowSize.width >= 1368 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạng thái đơn hàng')}
                  <KTBSDropdown
                    name="status"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(AgencyHelper.Data.orderStatusOptions, { value: filter?.status })
                          ?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.status}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...AgencyHelper.Data.orderStatusOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      handleRefresh({
                        ...filter,
                        page: 0,
                        status: value,
                      });
                    }}
                    isDropdownForm={true}
                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  />
                </div>
              )}

              {/* loc theo trang thai hop dong */}
              {windowSize.width >= 1630 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạng thái hợp đồng')}
                  <KTBSDropdown
                    name="contractStatus"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(AgencyHelper.Data.contractOptions, { value: filter?.contractStatus })
                          ?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.contractStatus}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...AgencyHelper.Data.contractOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      handleRefresh({
                        ...filter,
                        page: 0,
                        contractStatus: value,
                      });
                    }}
                    isDropdownForm={true}
                    direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  />
                </div>
              )}

              {/* loc theo khoang thoi gian */}
              {/* {windowSize.width >= 1368 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Khoảng thời gian')}
                  <CustomDateRangePicker
                    alignRight={true}
                    toggleFontWeight="400"
                    toggleElementBorderRadius={5}
                    initialValue={filter?.dateFilter}
                    onPressSave={(selectedValue, startDate, endDate) => {
                      handleRefresh({
                        ...filter,
                        page: 0,
                        dateFilter: selectedValue,
                        from: startDate,
                        to: endDate,
                      });
                    }}
                  />
                </div>
              )} */}

              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <ButtonFilterDropdown
                  direction={prefKey ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                  dropdownContent={
                    <AgencySaleOrderListFilterView
                      initialFilter={filter}
                      defaultOrderType={defaultOrderType}
                      onCancel={(newFilter) => {
                        handleRefresh(newFilter);
                      }}
                      onApply={(newFilter) => {
                        handleRefresh(newFilter);
                      }}
                    />
                  }
                />
                {additionalComponent}
                {prefKey && (
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                )}
              </div>
            </div>

            {/* table */}
            <div className="px-4">
              <KTTable
                columns={columns}
                items={agencySaleList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>

            {/* phan trang */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={total}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={agencySaleList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  handleRefresh({
                    ...filter,
                    page: iNewPage - 1,
                  });
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  handleRefresh({
                    ...filter,
                    limit: iNewRowsPerPage,
                    page: 0,
                  });
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="đơn hàng"
              />
            </div>
          </div>
        </div>,
        false,
        !showFullUI
      )}
    </div>
  );
}

export default AgencySaleOrderListView;
